import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import IconButton from '@material-ui/core/IconButton';
import InputBase from '@material-ui/core/InputBase';
import CircularProgress from '@material-ui/core/CircularProgress';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';

import {
  closeChatbotPanel,
  toggleChatbotPanel,
  useIsChatbotPanelOpen,
} from '../../../lib/stores/chatbot.store';
import {
  ErrorBanner,
  InputBar,
  LauncherFab,
  MessageBubble,
  MessageRow,
  MessagesScroll,
  PanelContainer,
  PanelHeader,
  PanelTitle,
  StarterChip,
  StartersWrap,
} from './chatbot.styles';
import { useReportCardChat } from './use-report-card-chat';
import { useChatStarters } from './use-chat-starters';
import { messageText, remainingStarters } from './chatbot.helpers';
import { ChatbotMessage } from './chatbot-message.component';


const RobotIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="3" r="1" fill="currentColor" />
    <line x1="12" y1="4" x2="12" y2="7" />
    <rect x="4" y="7" width="16" height="13" rx="3" />
    <circle cx="9" cy="13" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="15" cy="13" r="1.2" fill="currentColor" stroke="none" />
    <line x1="10" y1="17" x2="14" y2="17" />
  </svg>
);

export interface IReportCardChatWidgetProps {
  playerAssessmentId: string;
  /** ?token= from the URL when the visitor is unauthenticated. */
  token?: string;
  /** Bearer token for the proxy when the visitor is signed in. */
  authToken?: string;
}

export const ReportCardChatWidget: React.FC<IReportCardChatWidgetProps> = ({
  playerAssessmentId,
  token,
  authToken,
}) => {
  const isOpen = useIsChatbotPanelOpen();

  // Reset panel state on unmount so in-session navigations to a new report card start closed.
  useEffect(() => () => closeChatbotPanel(), []);

  const { messages, status, error, send, isStreaming } = useReportCardChat({
    playerAssessmentId,
    token,
    authToken,
  });

  const { starters } = useChatStarters({ enabled: isOpen, authToken, playerAssessmentId, token });

  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to bottom on every chunk. Keying on messages.length AND trailing
  // text length covers both "new message arrived" and "streaming text grew."
  const trailingLen = messages.length
    ? messageText(messages[messages.length - 1]).length
    : 0;

  useEffect(() => {
    if (!isOpen || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [isOpen, messages.length, trailingLen]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!draft.trim() || isStreaming) return;
      const text = draft;
      setDraft('');
      send(text);
    },
    [draft, isStreaming, send]
  );

  const handleStarterClick = useCallback(
    (text: string) => {
      if (isStreaming) return;
      send(text);
    },
    [isStreaming, send]
  );

  // Chips persist mid-conversation, filtered to starters the user hasn't asked yet;
  // a used starter drops out and the strip disappears once all are exhausted.
  const remaining = useMemo(
    () => remainingStarters(messages, starters),
    [messages, starters],
  );
  const showStarters = remaining.length > 0;

  return (
    <>
      <LauncherFab
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        onClick={() => toggleChatbotPanel()}
      >
        {isOpen ? <CloseIcon /> : <RobotIcon />}
      </LauncherFab>

      {isOpen && (
        <PanelContainer elevation={6} role="dialog" aria-label="Report card chat">
          <PanelHeader>
            <PanelTitle>Ask Max about this report card</PanelTitle>
            <IconButton aria-label="Close chat" size="small" onClick={() => closeChatbotPanel()}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </PanelHeader>

          <MessagesScroll ref={scrollRef}>
            {messages.length === 0 && (
              <MessageRow role="assistant">
                <MessageBubble role="assistant">
                  Hi! Ask me anything about this player's report card.
                </MessageBubble>
              </MessageRow>
            )}
            {messages.map((m) => {
              const text = messageText(m);
              return (
                <MessageRow key={m.id} role={m.role}>
                  <MessageBubble role={m.role}>
                    {text
                      ? <ChatbotMessage text={text} />
                      : (status === 'streaming' ? <CircularProgress size={14} /> : '')}
                  </MessageBubble>
                </MessageRow>
              );
            })}
          </MessagesScroll>

          {error && <ErrorBanner role="alert">{error}</ErrorBanner>}

          {showStarters && (
            <StartersWrap role="group" aria-label="Suggested questions">
              {remaining.map((text) => (
                <StarterChip
                  key={text}
                  type="button"
                  disabled={isStreaming}
                  onClick={() => handleStarterClick(text)}
                >
                  {text}
                </StarterChip>
              ))}
            </StartersWrap>
          )}

          <InputBar onSubmit={handleSubmit}>
            <InputBase
              fullWidth
              placeholder="Ask a question…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              inputProps={{ 'aria-label': 'Chat message' }}
              disabled={isStreaming}
              autoFocus
            />
            <IconButton
              type="submit"
              aria-label="Send message"
              color="primary"
              disabled={!draft.trim() || isStreaming}
            >
              {isStreaming ? <CircularProgress size={20} /> : <SendIcon />}
            </IconButton>
          </InputBar>
        </PanelContainer>
      )}
    </>
  );
};
